<template>
  <div class="w-full">
    <div class="w-full max-w-[450px] mx-auto">
      <h1 class="mb-6 text-[24px] font-medium leading-[28px] text-gray-800">
        Establish your copyright.
      </h1>

      <div class="mb-5">
        <div class="mb-3 flex items-center gap-x-2">
          <span class="text-[15px] font-medium leading-[18px] text-gray-800">
            Included with copyright registration
          </span>
          <Link
            size="md"
            icon="arrow"
            class="cursor-pointer"
            @click="handleLearnMore"
          >
            Learn more
          </Link>
        </div>
        <div
          class="flex flex-col gap-y-3 text-[15px] leading-[18px] text-gray-800"
        >
          <div class="flex items-center gap-x-3">
            <CheckmarkIcon class="size-4 shrink-0" aria-hidden="true" />
            <span>Official Certificate, court-ready</span>
          </div>
          <div class="flex items-center gap-x-3">
            <CheckmarkIcon class="size-4 shrink-0" aria-hidden="true" />
            <span>Recognized in 180+ countries (Berne &amp; TRIPS)</span>
          </div>
          <div class="flex items-center gap-x-3">
            <CheckmarkIcon class="size-4 shrink-0" aria-hidden="true" />
            <span>
              <DacrAIIcon
                class="inline-block h-[13px] align-[-1px]"
                aria-label="Dacr AI"
              />
              infringement detection
            </span>
          </div>
        </div>
      </div>

      <div class="mb-[30px]">
        <div class="mb-3 flex items-center gap-x-2">
          <span class="text-[15px] font-medium leading-[18px] text-gray-800">
            Included with Dacr Membership
          </span>
          <Link
            size="md"
            icon="arrow"
            class="cursor-pointer"
            @click="handleLearnMore"
          >
            Learn more
          </Link>
        </div>
        <div
          class="flex flex-col gap-y-3 text-[15px] leading-[18px] text-gray-800"
        >
          <div class="flex items-center gap-x-3">
            <CheckmarkIcon class="size-4 shrink-0" aria-hidden="true" />
            <span>Global community of protected creators</span>
          </div>
          <div class="flex items-center gap-x-3">
            <CheckmarkIcon class="size-4 shrink-0" aria-hidden="true" />
            <span>Dacr Membership card</span>
          </div>
          <div class="flex items-center gap-x-3">
            <CheckmarkIcon class="size-4 shrink-0" aria-hidden="true" />
            <span>Member-only events</span>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-y-2.5 mb-5">
        <template v-if="selectablePlans">
          <PlanOption
            v-for="plan in selectablePlans"
            v-model="selectedPlan"
            :key="plan.id"
            :plan="plan"
          />
        </template>

        <Skeleton
          v-else
          v-for="n in 2"
          :key="n"
          class="h-[128px] w-full rounded-[12px]"
        />
      </div>

      <Button
        class="w-full"
        variant="primary"
        size="lg"
        :disabled="!selectedPlan"
        @click="handleClick"
      >
        Continue
      </Button>

      <div
        v-if="props.type === SelectPlanType.Onboarding"
        class="mt-5 cursor-pointer text-center text-[14px] font-medium leading-[20px] text-gray-800"
        @click="handleFreeClick"
      >
        Try Basic for free
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  PlanItemStatus,
  PlanPriceType,
  useCompleteOnboardingMutation,
  usePlansQueryQuery,
  type Plan,
} from '@/shared/graphql/legacy/generated'
import { useBillingStore } from '@/features/billing/store'
import { NativeActionsEnum, useWebviewStore } from '@/shared/stores/webview'
import { useUserStore } from '@/shared/stores/user'
import { getTokens } from '@dacr/auth'
import { Button, Skeleton, Link } from '@dacr/library'
import { CheckmarkIcon, DacrAIIcon } from '@dacr/icons'
import { computed, ref, watch } from 'vue'
import PlanOption from './PlanOption.vue'
import { SelectPlanType } from '@/shared/types/index'
import { useRouter } from 'vue-router'
import { analytics } from '@/shared/services/analytics'
import { openDialog, closeDialog, Dialog } from '@/shared/dialogs/dialog'
import { openModal, Modals } from '@/shared/dialogs/modals'

const props = defineProps<{
  type: SelectPlanType
}>()

const router = useRouter()
const billingStore = useBillingStore()
const userStore = useUserStore()
const webviewStore = useWebviewStore()

const { result } = usePlansQueryQuery(
  { accessToken: getTokens().accessToken },
  { enabled: !webviewStore.isActive }
)

const { mutate: completeOnboarding } = useCompleteOnboardingMutation()

const plans = computed(() => result.value?.plans || billingStore.webviewPlans)

const subscriptionCycles = ['month', 'year']

const billingGroups = computed(() => plans.value?.groupByBillingCycle ?? [])

const selectableGroup = computed(() =>
  subscriptionCycles
    .map((cycle) =>
      billingGroups.value.find((group) => group.billingCycleType === cycle)
    )
    .find((group) => group?.plans.length)
)

const selectablePlans = computed(
  () => selectableGroup.value?.plans as Plan[] | undefined
)

const selectedPlan = ref<Plan>()

watch(
  selectablePlans,
  () => {
    if (!selectablePlans.value) return
    selectedPlan.value = selectablePlans.value[0]
  },
  { immediate: true }
)

const lowestActivePlanItem = computed(() =>
  selectedPlan.value?.items.find(
    (item, index, arr) =>
      item.status === PlanItemStatus.Active || index === arr.length - 1
  )
)

const handleLearnMore = () => {
  openDialog(Dialog.ProMembershipDetails)
}

const handleClick = () => {
  const alternateCycle = subscriptionCycles.find(
    (cycle) => cycle !== selectableGroup.value?.billingCycleType
  )

  const alternatePlan = billingGroups.value
    .find((group) => group.billingCycleType === alternateCycle)
    ?.plans.find((plan) => plan.name === selectedPlan.value?.name)

  if (!alternatePlan) {
    continueFlow()
    return
  }

  openDialog(Dialog.SelectPlanBilling, {
    props: { plans: [alternatePlan, selectedPlan.value] },
    events: {
      success(plan: unknown) {
        selectedPlan.value = plan as Plan
        closeDialog()
        continueFlow()
      },
    },
  })
}

const handleFreeClick = () => {
  analytics.track('onboarding_completed', {
    trigger: 'basic_link',
    source: webviewStore.isActive
      ? webviewStore.isiOS
        ? 'apple'
        : 'google'
      : 'web',
  })

  if (webviewStore.isActive) {
    return webviewStore.postMessage({
      type: NativeActionsEnum.Basic,
    })
  }

  completeOnboarding()
  return router.push('/home')
}

const continueFlow = async () => {
  if (!selectedPlan.value || !lowestActivePlanItem.value) return

  if (webviewStore.isActive) {
    return webviewStore.postMessage({
      type: NativeActionsEnum.PlanSelected,
      data: {
        productId: lowestActivePlanItem.value.nativeProductId,
        planId: lowestActivePlanItem.value.nativePlanId,
      },
    })
  }

  if (
    ![SelectPlanType.Register, SelectPlanType.Onboarding].includes(
      props.type
    ) &&
    !billingStore.canUpdateSubscription
  ) {
    return openDialog(Dialog.IAP, { props: { type: 'change' } })
  }

  if (
    props.type === SelectPlanType.Change &&
    lowestActivePlanItem.value.productId === billingStore.billing?.plan.id &&
    selectedPlan.value.items.length === 1
  ) {
    return router.push('/settings/billing')
  }

  const isSubscribedToBundlePlan = selectedPlan.value.items.some(
    (item) => billingStore.billing?.plan.id === item.productId
  )

  if (
    props.type === SelectPlanType.Change &&
    isSubscribedToBundlePlan &&
    selectedPlan.value.items.length > 1
  ) {
    return openModal(Modals.UpgradeBundleLimit)
  }

  const billingCycleType = lowestActivePlanItem.value.prices.find(
    (price) => price.type === PlanPriceType.Subscription
  )?.details?.billingCycleType

  billingStore.setPlanData({
    plan: selectedPlan.value,
    planItem: lowestActivePlanItem.value,
    billingCycle: {
      type: billingCycleType || 'month',
      description:
        plans.value?.groupByBillingCycle.find(
          (billingCycle) => billingCycle?.billingCycleType === billingCycleType
        )?.billingCycleDescription || '',
    },
  })

  if (
    props.type === SelectPlanType.Register ||
    props.type === SelectPlanType.Onboarding
  ) {
    userStore.updateUser({ totalFiles: 0 })
  }

  if (props.type === SelectPlanType.Change) {
    return router.push('/change-plans/confirm')
  }

  router.push(`/${props.type.toLowerCase()}/checkout`)
}
</script>
